import AppHeader from '@components/AppNavigation/AppHeader';
import { ActionsFooter, ApprovalDialogButton } from '@components/Forms';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Fade, Grid, Link, Typography } from '@mui/material';
import { HELP_LINKS } from '@utils/docs';
import { onSubmitForm } from '@utils/forms';
import { stringRequiredValidationScheme } from '@utils/validation';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import React, { FC, useEffect } from 'react';
import { IWebAuthnSettings } from 'types/settings';
import { useBoolean } from 'usehooks-ts';
import { object } from 'yup';
import AuthMethodSwitch from './AuthMethodSwitch';

const FORM_KEYS = {
	relyingPartyId: 'relyingPartyId'
};

const DEFAULT_FORM: IWebAuthnSettings = {
	id: '',
	version: 0,
	enabled: false,
	[FORM_KEYS.relyingPartyId]: ''
};

interface IWebAuthnProps {
	settings?: IWebAuthnSettings;
	onSave: (data: IWebAuthnSettings) => Promise<IWebAuthnSettings>;
}

const WebAuthnPage: FC<IWebAuthnProps> = ({ settings, onSave }) => {
	const { addToast } = useToast();
	const { value: showWarning, setTrue, setFalse } = useBoolean(false);

	const formik = useFormik<IWebAuthnSettings>({
		enableReinitialize: true,
		initialValues: {
			...DEFAULT_FORM,
			...settings
		},
		validationSchema: object({
			[FORM_KEYS.relyingPartyId]: stringRequiredValidationScheme()
		}),
		onSubmit: onSubmitForm<IWebAuthnSettings>(onSave, addToast)
	});

	useEffect(() => {
		const currentTopLevelDomain = settings?.relyingPartyId || '';
		if (formik.values.relyingPartyId !== currentTopLevelDomain) {
			setTrue();
		} else {
			setFalse();
		}
	}, [formik.values, settings, setTrue, setFalse]);

	return (
		<Box
			display="flex"
			flexDirection="column"
			flexGrow={1}
			overflow="hidden"
			data-testid="authentication-methods-webAuthn"
		>
			<AppHeader translation="pages.authMethods.webauthn.title" mb={0} />

			<Box display="flex" flexDirection="column" flexGrow={1} component="form">
				<Grid
					container
					spacing={2}
					pl={4}
					flexGrow={1}
					flexDirection="row"
					alignContent="flex-start"
				>
					<Grid item pl={4}>
						<AuthMethodSwitch formik={formik} />
					</Grid>

					<Grid item xs={12}>
						<Typography variant="body2">
							{intl.tr('authMethods.webAuthn.body.title')}
						</Typography>
					</Grid>

					<Grid item xs={8} flexGrow={1}>
						<TextFieldValidation
							id={FORM_KEYS.relyingPartyId}
							formik={formik}
							autoFocus
							required
							label={intl.tr('authMethods.webAuthn.relyingPartyId.input.title')}
							fullWidth
							placeholder={intl.tr(
								'authMethods.webAuthn.relyingPartyId.input.placeholder'
							)}
						/>
					</Grid>
					<Grid item xs={8} flexGrow={1}>
						<Fade in={showWarning}>
							<Alert
								severity="warning"
								data-testid="webauthn-top-level-domain-alert"
							>
								{intl.tr('authMethods.webAuthn.relyingPartyId.input.warning')}
								<Link
									target="_blank"
									href={`${HELP_LINKS.documentation}/customize/auth/webauthn/#top-level-domain`}
								>
									{intl.tr('lang.readmore')}
								</Link>
							</Alert>
						</Fade>
					</Grid>
				</Grid>
				<ActionsFooter>
					<LoadingButton
						color="primary"
						variant="contained"
						onClick={() => formik.handleSubmit()}
						loading={formik.isSubmitting}
						disabled={!formik.dirty}
						sx={{ marginRight: 1 }}
					>
						{intl.tr('button.save')}
					</LoadingButton>
					<ApprovalDialogButton
						buttonText={intl.tr('button.cancel')}
						onApprove={formik.resetForm}
						disabled={!formik.dirty}
						title={intl.tr('discard.changes.dialog.title')}
						question={intl.tr('discard.changes.dialog.question')}
						approveText={intl.tr('discard.changes.dialog.approve')}
					/>
				</ActionsFooter>
			</Box>
		</Box>
	);
};

WebAuthnPage.defaultProps = {
	settings: { id: '', version: 0, enabled: false }
};

export default WebAuthnPage;
